import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getProducts } from '@services/product.service'
import { useCartStore } from '@store/useCartStore'
import { useToastStore } from '@store/useToastStore'
import useFadeIn from '@hooks/useFadeIn'
import Spinner from '@components/UI/Spinner'
import './FeaturedProducts.scss'

const VISUAL_TYPES = ['sachet', 'hex', 'burner']
const BG_GRADIENTS = [
  'linear-gradient(135deg, var(--tan) 0%, var(--cream) 100%)',
  'linear-gradient(135deg, #EAD9C8, #F8F3ED)',
  'linear-gradient(135deg, #E8E0D8, #F5EEE6)',
]

function SachetVisual() {
  return (
    <div className="sachet-visual">
      <div className="sachet-ribbon" />
      <div className="petal" />
      <div className="petal" />
      <div className="petal" />
      <div className="mini-leaf" />
      <div className="mini-leaf" />
    </div>
  )
}

function HexVisual() {
  return (
    <div className="hex-visual">
      <div className="petal" />
      <div className="petal" />
    </div>
  )
}

function BurnerVisual() {
  return (
    <div className="burner-visual">
      <div className="burner-top" />
      <div className="melt-chunk c1" />
      <div className="melt-chunk c2" />
      <div className="melt-chunk c3" />
      <div className="burner-arch" />
    </div>
  )
}

const VISUALS = { sachet: SachetVisual, hex: HexVisual, burner: BurnerVisual }

function ProductCard({ product, visualType, bgGradient, delay }) {
  const ref = useFadeIn()
  const addItem = useCartStore((s) => s.addItem)
  const addToast = useToastStore((s) => s.addToast)
  const Visual = VISUALS[visualType] || SachetVisual

  const handleAdd = (e) => {
    e.preventDefault()
    addItem(product, null, 1)
    addToast({ message: `${product.name} adăugat în coș`, type: 'success' })
  }

  return (
    <div className="prod-card fade-up" ref={ref} style={{ transitionDelay: `${delay}s` }}>
      <Link to={`/product/${product.slug}`} className="prod-img">
        <div className="prod-img-bg" style={{ background: bgGradient }} />
        <div className="prod-visual">
          <Visual />
        </div>
      </Link>
      <div className="prod-info">
        <div className="prod-tag">
          {product.short_description || 'Odorizant decorativ'}
        </div>
        <div className="prod-name">{product.name}</div>
        <p className="prod-desc">
          {product.description?.slice(0, 120)}
          {product.description?.length > 120 ? '…' : ''}
        </p>
        <div className="prod-footer">
          <span className="prod-price">{product.price} lei</span>
          <button className="prod-btn" onClick={handleAdd}>Adaugă</button>
        </div>
      </div>
    </div>
  )
}

export default function FeaturedProducts() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    getProducts({ isFeatured: true, pageSize: 3 })
      .then((res) => {
        if (!cancelled) setProducts(res.data?.items || [])
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  return (
    <section className="products" id="produse">
      <div className="section-label">Colecția noastră</div>
      <h2 className="section-title">
        Piese <em>artizanale</em><br />cu suflet
      </h2>

      {loading ? (
        <Spinner />
      ) : products.length > 0 ? (
        <div className="products-grid">
          {products.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              visualType={VISUAL_TYPES[i % VISUAL_TYPES.length]}
              bgGradient={BG_GRADIENTS[i % BG_GRADIENTS.length]}
              delay={i * 0.12}
            />
          ))}
        </div>
      ) : (
        <div className="products-grid">
          {FALLBACK_PRODUCTS.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              visualType={VISUAL_TYPES[i]}
              bgGradient={BG_GRADIENTS[i]}
              delay={i * 0.12}
            />
          ))}
        </div>
      )}
    </section>
  )
}

const FALLBACK_PRODUCTS = [
  { id: 'f1', name: 'Sachet Floral Rotund', slug: 'sachet-floral-rotund', price: 35, description: 'Ceară naturală de soia parfumată cu flori uscate. Perfect pentru dulapuri, sertare sau spații mici.', short_description: 'Odorizant decorativ' },
  { id: 'f2', name: 'Sachet Hexagonal', slug: 'sachet-hexagonal', price: 38, description: 'Formă hexagonală elegantă, decorată cu flori de bujor și frunzuliță. Aromă florală subtilă și persistentă.', short_description: 'Odorizant decorativ' },
  { id: 'f3', name: 'Set Wax Melts + Lampă', slug: 'set-wax-melts-lampa', price: 89, description: 'Lampă ceramică alb mat și wax melts parfumate. Cadoul perfect pentru orice ocazie, ambalat elegant.', short_description: 'Set cadou' },
]
